import type { FileCreateMessageDatagramBuf } from "~/classes/generated/message"
import type { FileCreateResponseDatagramBuf } from "~/classes/generated/response"

import { InterfaceFile } from "~/classes/file/InterfaceFile"
import { DatagramBuf } from "~/classes/generated/datagram"
import { FileCreateRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { addFileToBuf, parseFile } from "~/utils/flatbuffers/file"
import { addFileIdToBuf, parseFileId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("file/create")

export function parseFileCreateRequestDatagramBuf(
  datagram: FileCreateRequestDatagramBuf,
): FileCreateRequestDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    file: parseFile(datagram.file()!),
    offline: datagram.offline(),
    preHash: parseFileId(datagram.preHash()!),
  }
}

export function sendFileCreateRequest(request: FileCreateRequestDatagram) {
  const { sendWorkerDatagramBuf } = useCache()

  const fbb = new DatagramBufferBuilder(64)
  const preHash = addFileIdToBuf(fbb, request.preHash)
  const requestId = addRequestIdToBuf(fbb, request.requestId)
  const file = addFileToBuf(fbb, request.file)
  FileCreateRequestDatagramBuf.startFileCreateRequestDatagramBuf(fbb)
  FileCreateRequestDatagramBuf.addRequestId(fbb, requestId)
  FileCreateRequestDatagramBuf.addFile(fbb, file)
  FileCreateRequestDatagramBuf.addOffline(fbb, request.offline)
  FileCreateRequestDatagramBuf.addPreHash(fbb, preHash)
  const createRequest = FileCreateRequestDatagramBuf.endFileCreateRequestDatagramBuf(fbb)

  RequestDatagramBuf.startRequestDatagramBuf(fbb)
  RequestDatagramBuf.addFileCreateRequest(fbb, createRequest)
  const requests = DatagramBuf.createRequestVector(fbb, [RequestDatagramBuf.endRequestDatagramBuf(fbb)])
  DatagramBuf.startDatagramBuf(fbb)
  DatagramBuf.addRequest(fbb, requests)
  fbb.finish(DatagramBuf.endDatagramBuf(fbb))
  sendWorkerDatagramBuf(fbb.asUint8Array())
}

export function parseFileCreateResponseDatagramBuf(
  datagram: FileCreateResponseDatagramBuf,
): FileCreateResponseDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    file: maybe(parseFile)(datagram.file()),
    offline: datagram.offline(),
    fileId: parseFileId(datagram.fileId()!),
  }
}

/** Parse a Flatbuffers representation of a response */
export function handleFileCreateResponseDatagramBuf(
  datagram: FileCreateResponseDatagramBuf,
) {
  onFileCreateResponse(parseFileCreateResponseDatagramBuf(datagram))
}

export function onFileCreateResponse(response: FileCreateResponseDatagram, quiet = false) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  emitInterfaceEvent("fileCreateResponse", response, response.requestId)

  if (response.error)
    return onFileCreateErrored(response.error, quiet)
  if (!response.file)
    throw new Error("No file in create response")

  const iFile = new InterfaceFile(response.file)
  const parentId = iFile.inferredParentId()
  if (!parentId) {
    console.error("No parent id in file create response", response)
    throw new Error("no parent id in file create response")
  }

  emitEntityEvent("fileProperties", iFile.fileId, iFile.inner(), response.offline)
  emitEntityEvent("fileCreated", iFile.fileId, iFile.inner(), response.offline)
  emitEntityEvent("childProperties", parentId, iFile.inner(), response.offline)
  emitEntityEvent("childCreated", parentId, iFile.inner(), response.offline)
}

function onFileCreateErrored(error: DatagramError, quiet = false) {
  const { notifyDatagramError } = useAlerts()

  console.error("File create error:", error)

  if (!quiet)
    return notifyDatagramError(error, "File creation was unsuccessful")
}

export function handleFileCreateMessageDatagramBuf(datagram: FileCreateMessageDatagramBuf) {
  onFileCreateMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    file: parseFile(datagram.file()!),
  })
}

export function onFileCreateMessage(datagram: FileCreateMessageDatagram) {
  const { emitEntityEvent } = useEvents()

  const iFile = new InterfaceFile(datagram.file)

  emitEntityEvent("fileProperties", iFile.fileId, iFile.inner(), false)
  emitEntityEvent("fileCreated", iFile.fileId, iFile.inner(), false)

  const parentId = iFile.inferredParentId()
  if (!parentId) {
    console.error("No parent id in file create response", datagram)
    throw new Error("no parent id in file create response")
  }
  emitEntityEvent("childProperties", parentId, iFile.inner(), false)
  emitEntityEvent("childCreated", parentId, iFile.inner(), false)
}
